import { useCallback, useMemo } from 'react'
import { useHotkeys, useHotkeysContext } from 'react-hotkeys-hook'
import { COMMENT_ESC_SCOPE_PREFIX } from '@scene'
import { some } from 'lodash-es'
import { useEditElementStore } from '@editorStores'
import { useIfcMode, useIfcSelection, useSelectionMode } from '@editorHooks'

/**
 * Escape handling for the IFC Import page: cancels an active selection mode (e.g. the add-comment
 * tool) first, otherwise deactivates the "Gebäude" building tool. Disabled while a comment edit
 * owns Escape (its own scope), so it never steals that key.
 */
const useIFCImportEsc = (): void => {
  const { isSelectionMode, unsetSelectionMode } = useSelectionMode()

  const { ifcMode, setIfcMode } = useIfcMode()
  const { selectedIfcElementGuids, setSelectedIfcElementGuids } = useIfcSelection()
  const setMultiActiveElements = useEditElementStore(state => state.setMultiActiveElements)
  const multiActiveElements = useEditElementStore(state => state.multiActiveElements)
  const setMultiActiveOpenings = useEditElementStore(state => state.setMultiActiveOpenings)
  const multiActiveOpenings = useEditElementStore(state => state.multiActiveOpenings)

  const activeElement = useEditElementStore(state => state.activeElement)
  const setActiveElement = useEditElementStore(state => state.setActiveElement)

  const { enabledScopes } = useHotkeysContext()

  const commentScopeEnabled = useMemo(
    () => some(enabledScopes, scope => scope.startsWith(COMMENT_ESC_SCOPE_PREFIX)),
    [enabledScopes],
  )

  const escKeyPress = useCallback(() => {
    if (isSelectionMode) unsetSelectionMode()
    else if (multiActiveOpenings.length) setMultiActiveOpenings([])
    else if (multiActiveElements.length) setMultiActiveElements([])
    else if (selectedIfcElementGuids.length) setSelectedIfcElementGuids([])
    else if (activeElement) setActiveElement(null)
    else if (ifcMode && ifcMode !== 'guided') setIfcMode(null)
  }, [
    isSelectionMode,
    unsetSelectionMode,
    multiActiveOpenings.length,
    setMultiActiveOpenings,
    multiActiveElements.length,
    setMultiActiveElements,
    selectedIfcElementGuids.length,
    setSelectedIfcElementGuids,
    activeElement,
    setActiveElement,
    ifcMode,
    setIfcMode,
  ])

  useHotkeys('esc', escKeyPress, { enabled: !commentScopeEnabled }, [escKeyPress])
}

export default useIFCImportEsc
